<script setup>
import { computed } from 'vue';
import Modal from '@/Components/Modal.vue';
import { openUrlWithFakeTag } from '@/Services/agendartMobileAppUtils';
import { getSocialUrl, getWhatsAppUrl } from '@/Services/websiteContactUtils';

const props = defineProps({
    visible: Boolean,
    web_site: Object,
    selfSchedulingLink: String,
});

const emit = defineEmits(['close']);
const color = computed(() => props.web_site?.color || 'var(--agendart-primary)');

const phone = computed(() =>
    props.web_site?.contact?.telephone ||
    props.web_site?.professional_contact_phone ||
    ''
);
const whatsapp = computed(() => props.web_site?.contact?.whats_app || '');
const email = computed(() => props.web_site?.contact?.email || '');
const address = computed(() => props.web_site?.text?.address || '');
const addressUrl = computed(() => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.value)}`);

const whatsAppUrl = computed(() => getWhatsAppUrl(whatsapp.value));
const socialLinks = computed(() => [
    { key: 'instagram', label: 'Instagram', icon: 'fa-brands fa-instagram' },
    { key: 'facebook', label: 'Facebook', icon: 'fa-brands fa-facebook-f' },
    { key: 'linkedin', label: 'LinkedIn', icon: 'fa-brands fa-linkedin-in' },
    { key: 'youtube', label: 'YouTube', icon: 'fa-brands fa-youtube' },
    { key: 'twitter', label: 'X', icon: 'fa-brands fa-x-twitter' },
].map((social) => ({
    ...social,
    value: props.web_site?.contact?.[social.key] || '',
    url: getSocialUrl(social.key, props.web_site?.contact?.[social.key]),
})).filter((social) => social.value));

const openSelfScheduling = () => {
    if (props.selfSchedulingLink) {
        openUrlWithFakeTag(props.selfSchedulingLink, '_blank');
    }
};
</script>

<template>
    <Modal :show="visible" maxWidth="md" @close="emit('close')">
        <div class="p-5 sm:p-7">
            <div class="flex justify-between items-start gap-3">
                <div>
                    <h2 class="text-lg font-semibold text-gray-900">{{ $t('service_information') }}</h2>

                </div>
                <button type="button" class="text-gray-500 hover:text-gray-900 p-1"
                    aria-label="Fechar" @click="emit('close')">
                    <i class="pi pi-times"></i>
                </button>
            </div>

            <div class="mt-5 flex flex-col gap-3">
                <a v-if="phone" :href="`tel:${phone.replace(/[^0-9+]/g, '')}`"
                    class="flex items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50">
                    <i class="pi pi-phone text-lg" :style="{ color }"></i>
                    <div class="min-w-0">
                        <p class="text-xs text-gray-500">Telefone</p>
                        <p class="text-sm font-medium text-gray-900 break-all">{{ phone }}</p>
                    </div>
                </a>

                <a v-if="whatsapp" :href="whatsAppUrl" target="_blank" rel="noopener noreferrer"
                    class="flex items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50">
                    <i class="pi pi-whatsapp text-lg text-green-600"></i>
                    <div class="min-w-0">
                        <p class="text-xs text-gray-500">WhatsApp</p>
                        <p class="text-sm font-medium text-gray-900 break-all">{{ whatsapp }}</p>
                    </div>
                </a>

                <a v-if="email" :href="`mailto:${email}`"
                    class="flex items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50">
                    <i class="pi pi-envelope text-lg" :style="{ color }"></i>
                    <div class="min-w-0">
                        <p class="text-xs text-gray-500">E-mail</p>
                        <p class="text-sm font-medium text-gray-900 break-all">{{ email }}</p>
                    </div>
                </a>

                <a v-if="address" :href="addressUrl" target="_blank" rel="noopener noreferrer"
                    class="flex items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50">
                    <i class="pi pi-map-marker text-lg" :style="{ color }"></i>
                    <div class="min-w-0">
                        <p class="text-xs text-gray-500">Endereço</p>
                        <p class="break-all text-sm font-medium text-gray-900">{{ address }}</p>
                    </div>
                </a>

                <a v-for="social in socialLinks" :key="social.key" :href="social.url"
                    target="_blank" rel="noopener noreferrer"
                    class="flex items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50">
                    <i :class="social.icon" class="text-lg" :style="{ color }"></i>
                    <div class="min-w-0">
                        <p class="text-xs text-gray-500">{{ social.label }}</p>
                        <p class="break-all text-sm font-medium text-gray-900">{{ social.value }}</p>
                    </div>
                </a>

                <p v-if="!phone && !whatsapp && !email && !address && socialLinks.length === 0"
                    class="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                    Este profissional ainda não informou canais de contato.
                </p>
            </div>

            <button v-if="selfSchedulingLink" type="button"
                class="mt-6 w-full py-3 rounded-lg text-white font-semibold"
                :style="{ backgroundColor: color }" @click="openSelfScheduling">
                <i class="pi pi-calendar mr-2"></i>{{ $t('schedule') }}
            </button>
        </div>
    </Modal>
</template>
